/**
 * "Diurnal QC" on a project in Teslyn Cloud (help `cloud.diurnal-qc`; docs/specs/cloud/diurnal-qc-cloud.md; Diurnal
 * parity C3, 02-060). Staff see the project's TA Live base stations (field crew, managers, data processors and tenant
 * admins tick which), and each day Teslyn Cloud worked out with Teslyn Field's rules and the project's own Diurnal QC
 * values: the chart (the figure Field's screenshot and daily PDF draw, `renderDiurnalFigureSvg`, today redrawn every
 * minute), the counts, each exceedance run, gap and run of bad data in Field's words, and the side-by-side run. Report
 * publishers (decision 184) show a day to the client and stop showing it. The client (and Preview as client) sees
 * only the days shown to them, read only, drawn in the External colours of the daily PDF (decision 269); cloud QC
 * reads TA Live only, never observatory or BoM data (decisions 340, 341).
 */
import { useCallback, useEffect, useMemo, useState, type ReactElement, type ReactNode } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  CLOUD_QC_ITEM_WORDS,
  CLOUD_QC_STATUS_WORDS,
  canLinkCloudQcStation,
  canPublishCloudQcDay,
  cloudQcClock,
  cloudQcCountsText,
  cloudQcSettingsText,
  formatOffset,
  qcFromCloudChart,
  renderDiurnalFigureSvg,
  type CloudQcChart,
  stationLinkDates,
  type CloudQcDayRow,
  type StationLinkRow,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import type { PortalProject } from './api.ts';
import {
  loadChart,
  loadDays,
  loadLinks,
  loadPublications,
  loadStations,
  publishDay,
  setLink,
  withdrawDay,
  type DiurnalPublication,
  type DiurnalStation,
} from './diurnalApi.ts';
import { SideBySidePanel } from './SideBySidePanel.tsx';

/** Today's day is drawn again this often while the tab is open (the cloud works it out every 5 to 15 minutes). */
export const DIURNAL_TAB_REFRESH_MS = 60_000;

const message = (e: unknown): string => (e instanceof Error ? e.message : String(e));

/** "27 Oct 10:42" in the project time zone. */
function when(iso: string | null, offsetH: number): string {
  if (!iso) return '—';
  const ms = Date.parse(iso);
  if (!Number.isFinite(ms)) return '—';
  const d = new Date(ms + Math.round(offsetH * 3_600_000));
  return `${d.getUTCDate()} ${d.toLocaleString('en-AU', { month: 'short', timeZone: 'UTC' })} ${d.toISOString().slice(11, 16)}`;
}

const duration = (ms: number): string => {
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s} s` : `${Math.floor(s / 60)} min ${s % 60} s`;
};

/** The figure as an image (the SVG text is drawn by the domain with every text escaped). */
function Figure({
  day,
  chart,
  project,
  use,
}: {
  readonly day: CloudQcDayRow;
  readonly chart: CloudQcChart;
  readonly project: PortalProject;
  readonly use: 'internal' | 'external';
}): ReactElement {
  const src = useMemo(() => {
    if (chart.t.length === 0) return null;
    const svg = renderDiurnalFigureSvg({
      tenantName: '',
      project: project.number,
      base: day.station_name,
      offsetH: day.offset_h,
      datasets: [{ label: day.station_name, qc: qcFromCloudChart(chart, day.settings), family: 'ta_live' }],
      primary: 0,
      lines: [],
      settings: day.settings as never,
      use,
      formatLabel: 'TA Live',
    });
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, [day, chart, project.number, use]);
  if (!src) return <p className="body muted">No readings on this day yet.</p>;
  return (
    <img
      className="diurnal-figure"
      src={src}
      alt={`Diurnal QC chart of ${day.station_name} on ${day.day}: ${cloudQcCountsText(day.counts)}`}
    />
  );
}

function Items({ day }: { readonly day: CloudQcDayRow }): ReactElement {
  if (day.items.length === 0) return <p className="body">No exceedances, gaps or bad data on this day.</p>;
  return (
    <div className="table-wrap">
      <table className="table" aria-label={`Exceedances, gaps and bad data, ${day.station_name}, ${day.day}`}>
        <thead>
          <tr>
            <th className="label">What</th>
            <th className="label num">From (UTC{formatOffset(day.offset_h)})</th>
            <th className="label num">To</th>
            <th className="label num">Length</th>
            <th className="label">Details</th>
          </tr>
        </thead>
        <tbody>
          {day.items.map((i) => (
            <tr key={i.key}>
              <td>
                <span className={i.kind === 'exceedance' ? 'qc qc--fail label' : 'qc qc--warn label'}>
                  {CLOUD_QC_ITEM_WORDS[i.kind]}
                </span>
                {i.ongoing && <span className="caption"> still going</span>}
              </td>
              <td className="num data">{cloudQcClock(i.start_ms, day.offset_h)}</td>
              <td className="num data">{cloudQcClock(i.end_ms, day.offset_h)}</td>
              <td className="num data">{duration(i.end_ms - i.start_ms)}</td>
              <td className="table__text">{i.details}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DayPanel({
  sb,
  project,
  day,
  table,
  use,
  refreshKey,
  children,
}: {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly day: CloudQcDayRow;
  readonly table: 'diurnal_qc_day' | 'diurnal_qc_publication';
  readonly use: 'internal' | 'external';
  readonly refreshKey: string;
  readonly children?: ReactNode;
}): ReactElement {
  const [chart, setChart] = useState<CloudQcChart | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    loadChart(sb, table, day.id)
      .then((c) => live && setChart(c))
      .catch((e: unknown) => live && setError(message(e)));
    return () => {
      live = false;
    };
  }, [sb, table, day.id, refreshKey]);
  return (
    <div className="panel stack" aria-label={`${day.station_name}, ${day.day}`}>
      <div className="row row--wrap">
        <h3 className="heading">
          {day.station_name} · {day.day}
        </h3>
        {table === 'diurnal_qc_day' && !day.final && (
          <span className={day.status === 'online' ? 'chip chip--pass' : 'chip chip--fail'}>
            {CLOUD_QC_STATUS_WORDS[day.status]}
          </span>
        )}
      </div>
      <p className="body data">{cloudQcCountsText(day.counts)}</p>
      <p className="caption muted">
        {day.readings.toLocaleString('en-AU')} readings, {when(day.first_at, day.offset_h)} to{' '}
        {when(day.last_at, day.offset_h)} (UTC{formatOffset(day.offset_h)}) · worked out{' '}
        {when(day.computed_at, day.offset_h)}
        {day.final ? '' : ', updated as readings arrive'}
      </p>
      <p className="caption muted">{cloudQcSettingsText(day.settings)}</p>
      {error ? (
        <ErrorState message={error} />
      ) : chart ? (
        <Figure day={day} chart={chart} project={project} use={use} />
      ) : (
        <Loading what="the chart" />
      )}
      {children}
      <Items day={day} />
    </div>
  );
}

export interface DiurnalTabProps {
  readonly sb: SupabaseClient;
  readonly project: PortalProject;
  readonly mode: 'staff' | 'client';
  readonly roles?: readonly string[];
  /** The workbook inflater of the side-by-side run (tests pass Node's). */
  readonly inflate?: (data: Uint8Array) => Promise<Uint8Array>;
}

export function DiurnalTab({ sb, project, mode, roles = [], inflate }: DiurnalTabProps): ReactElement {
  const staff = mode === 'staff';
  const live = project.status !== 'archived';
  const mayLink = staff && live && canLinkCloudQcStation(roles);
  const mayPublish = staff && live && canPublishCloudQcDay(roles);
  const [stations, setStations] = useState<DiurnalStation[] | null>(null);
  const [links, setLinks] = useState<StationLinkRow[]>([]);
  const [dates_, setDates] = useState<Record<string, { starts: string; ends: string }>>({});
  const [days, setDays] = useState<CloudQcDayRow[] | null>(null);
  const [pubs, setPubs] = useState<DiurnalPublication[] | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  const reload = useCallback(() => setRound((r) => r + 1), []);

  useEffect(() => {
    let on = true;
    void (async () => {
      try {
        if (staff) {
          const st = await loadStations(sb, project.tenantId);
          const names = new Map(st.map((s) => [s.id, s.name]));
          const [l, d, p] = await Promise.all([
            loadLinks(sb, project.id),
            loadDays(sb, project.id, names),
            loadPublications(sb, project.id),
          ]);
          if (!on) return;
          setStations(st);
          setLinks(l);
          setDays(d);
          setPubs(p);
        } else {
          const p = await loadPublications(sb, project.id);
          if (!on) return;
          setPubs(p);
          setDays(p);
        }
      } catch (e) {
        if (on) setError(message(e));
      }
    })();
    return () => {
      on = false;
    };
  }, [sb, project.id, project.tenantId, staff, round]);

  // today's day changes while readings arrive: draw it again every minute (staff)
  useEffect(() => {
    if (!staff) return undefined;
    const t = setInterval(reload, DIURNAL_TAB_REFRESH_MS);
    return () => clearInterval(t);
  }, [staff, reload]);

  const dates = useMemo(() => [...new Set((days ?? []).map((d) => d.day))].sort().reverse(), [days]);
  const date = chosen && dates.includes(chosen) ? chosen : (dates[0] ?? null);
  const shown = (days ?? []).filter((d) => d.day === date);

  async function toggle(
    s: DiurnalStation,
    on: boolean,
    starts: string | null = null,
    ends: string | null = null,
  ): Promise<void> {
    setBusy(`link:${s.id}`);
    setNotice(null);
    try {
      await setLink(sb, project.id, s.id, on, starts, ends);
      const l = links.find((x) => x.station_id === s.id);
      setNotice({
        ok: true,
        text: !on
          ? `${s.name} is no longer linked to ${project.number}; the days worked out are kept.`
          : l
            ? `${s.name} works on ${project.number} ${stationLinkDates({ starts_on: starts ?? l.starts_on, ends_on: ends })}.`
            : `${s.name} is linked to ${project.number} from today: Teslyn Cloud works out its diurnal QC within 5 minutes.`,
      });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  async function publish(d: CloudQcDayRow): Promise<void> {
    setBusy(`pub:${d.id}`);
    setNotice(null);
    try {
      await publishDay(sb, d.id);
      setNotice({ ok: true, text: `${d.station_name}, ${d.day} is shown to the client as it is now.` });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  async function withdraw(p: DiurnalPublication): Promise<void> {
    setBusy(`wd:${p.id}`);
    setNotice(null);
    try {
      await withdrawDay(sb, p.id);
      setNotice({ ok: true, text: `${p.station_name}, ${p.day} is no longer shown to the client.` });
      reload();
    } catch (e) {
      setNotice({ ok: false, text: message(e) });
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState message={error} />;
  if (days === null || (staff && stations === null)) return <Loading what="the diurnal QC" />;

  return (
    <div className="stack">
      {staff && (
        <div className="panel stack" aria-label="Base stations">
          <h2 className="heading">Base stations</h2>
          <p className="caption muted">
            The TA Live units working on this project, with their dates (set by managers and tenant admins
            when a unit goes to the job). Teslyn Cloud works out the diurnal QC of each from its TA Live
            readings with this project’s Diurnal QC values on those days, sends the diurnal alerts to the
            project’s field crew, and checks the unit’s clock is on the project’s time zone.
          </p>
          {stations!.length === 0 ? (
            <p className="body">No TA Live units are set up for your organisation yet (Station health).</p>
          ) : (
            <ul className="stack">
              {stations!.map((s) => {
                const l = links.find((x) => x.station_id === s.id) ?? null;
                const d = dates_[s.id] ?? { starts: l?.starts_on ?? '', ends: l?.ends_on ?? '' };
                const label = `${s.name}${s.station_number ? ` (${s.station_number})` : ''}`;
                return (
                  <li key={s.id} className="row row--wrap">
                    <label className="row">
                      <input
                        type="checkbox"
                        checked={l !== null}
                        disabled={!mayLink || busy !== null}
                        onChange={(e) => void toggle(s, e.target.checked)}
                      />
                      <span className="body">{label}</span>
                    </label>
                    {l && !mayLink && <span className="caption muted">{stationLinkDates(l)}</span>}
                    {l && mayLink && (
                      <>
                        <label className="row">
                          <span className="label">From</span>
                          <input
                            type="date"
                            aria-label={`${label} from`}
                            value={d.starts}
                            onChange={(e) =>
                              setDates({ ...dates_, [s.id]: { ...d, starts: e.target.value } })
                            }
                          />
                        </label>
                        <label className="row">
                          <span className="label">To</span>
                          <input
                            type="date"
                            aria-label={`${label} to`}
                            value={d.ends}
                            onChange={(e) => setDates({ ...dates_, [s.id]: { ...d, ends: e.target.value } })}
                          />
                        </label>
                        <Button
                          variant="quiet"
                          disabled={busy !== null || !d.starts}
                          onClick={() => void toggle(s, true, d.starts, d.ends || null)}
                        >
                          Save dates
                        </Button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
      {notice && (
        <p className={notice.ok ? 'notice' : 'notice notice--fail'} role={notice.ok ? 'status' : 'alert'}>
          {notice.text}
        </p>
      )}
      {dates.length === 0 ? (
        <div className="panel">
          <p className="body">
            {staff
              ? links.length === 0
                ? 'Link a base station to start cloud diurnal QC for this project.'
                : 'No day worked out yet: Teslyn Cloud works out the base stations within 5 minutes of their readings arriving.'
              : 'No diurnal QC has been shared for this project yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="row">
            <label className="row">
              <span className="label">Day</span>
              <select value={date ?? ''} onChange={(e) => setChosen(e.target.value)}>
                {dates.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </label>
            {staff && (
              <Button variant="quiet" onClick={reload}>
                Refresh
              </Button>
            )}
          </div>
          {shown.map((d) => {
            const pub = staff
              ? ((pubs ?? []).find((p) => p.station_id === d.station_id && p.day === d.day) ?? null)
              : null;
            return (
              <DayPanel
                key={d.id}
                sb={sb}
                project={project}
                day={d}
                table={staff ? 'diurnal_qc_day' : 'diurnal_qc_publication'}
                use={staff ? 'internal' : 'external'}
                refreshKey={`${d.computed_at}:${round}`}
              >
                {staff ? (
                  <div className="row row--wrap">
                    {pub ? (
                      <span className="caption">
                        Shown to the client since {when(pub.published_at, d.offset_h)}
                        {pub.computed_at !== d.computed_at ? ' (an earlier version of this day)' : ''}
                      </span>
                    ) : (
                      <span className="caption muted">Not shown to the client</span>
                    )}
                    {mayPublish && (
                      <Button
                        busy={busy === `pub:${d.id}`}
                        disabled={busy !== null}
                        onClick={() => void publish(d)}
                      >
                        {pub ? 'Show this version to the client' : 'Show to the client'}
                      </Button>
                    )}
                    {mayPublish && pub && (
                      <Button
                        variant="quiet"
                        busy={busy === `wd:${pub.id}`}
                        disabled={busy !== null}
                        onClick={() => void withdraw(pub)}
                      >
                        Stop showing the client
                      </Button>
                    )}
                  </div>
                ) : (
                  <p className="caption muted">
                    Shared {when((d as DiurnalPublication).published_at, d.offset_h)}
                  </p>
                )}
              </DayPanel>
            );
          })}
        </>
      )}
      {staff && dates.length > 0 && (
        <SideBySidePanel
          sb={sb}
          project={project}
          days={days}
          roles={roles}
          {...(inflate ? { inflate } : {})}
        />
      )}
    </div>
  );
}
